import { useCallback, useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import { Check, ChevronLeft, ChevronRight, FileText, Upload, X } from 'lucide-react';
import type { CsvImportEntity } from '@peles/shared';
import { EntityModal } from './EntityModal';
import { useCsvImport } from '../../hooks/useCsvImport';

export type CsvImportFieldType = 'string' | 'number' | 'boolean' | 'enum';

export interface CsvImportFieldSchema {
  /** Target field key on the entity (e.g. `firstName`). */
  key: string;
  /** Human-readable label for the mapping UI. */
  label: string;
  required?: boolean;
  type: CsvImportFieldType;
  /** Allowed values when type === 'enum'. Case-insensitive match against CSV cell text. */
  enumValues?: readonly string[];
  /**
   * Aliases used when auto-suggesting a column ↔ field mapping. Compared
   * case-insensitively after stripping non-alphanumeric characters.
   */
  aliases?: readonly string[];
}

export interface CsvImportWizardConfig {
  entity: CsvImportEntity;
  /** Title shown in the modal header. */
  title: string;
  fields: readonly CsvImportFieldSchema[];
}

export interface CsvImportWizardProps {
  open: boolean;
  onClose: () => void;
  config: CsvImportWizardConfig;
  /** When set, the wizard runs in event-scoped mode and skips the target step. */
  scope?: { eventId?: string };
  /** Fired on successful import with the result so callers can show toasts, refresh lists, etc. */
  onImported?: (result: { created: number; skipped: number }) => void;
}

interface ParsedCsv {
  headers: string[];
  rows: Array<Record<string, string>>;
  fileName: string;
}

type Step = 'file' | 'mapping' | 'preview' | 'result';

const SKIP_VALUE = '__skip__';

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function autoMap(
  headers: string[],
  fields: readonly CsvImportFieldSchema[],
): Record<string, string> {
  const mapping: Record<string, string> = {};
  const usedHeaders = new Set<string>();
  for (const field of fields) {
    const candidates = [field.key, field.label, ...(field.aliases ?? [])].map(normalize);
    const match = headers.find((h) => !usedHeaders.has(h) && candidates.includes(normalize(h)));
    if (match) {
      mapping[field.key] = match;
      usedHeaders.add(match);
    } else {
      mapping[field.key] = SKIP_VALUE;
    }
  }
  return mapping;
}

function coerceValue(
  raw: string | undefined,
  field: CsvImportFieldSchema,
): { ok: true; value: unknown } | { ok: false; message: string } {
  const trimmed = (raw ?? '').trim();
  if (trimmed === '') {
    if (field.required) return { ok: false, message: `${field.label} is required` };
    return { ok: true, value: undefined };
  }
  switch (field.type) {
    case 'string':
      return { ok: true, value: trimmed };
    case 'number': {
      const n = Number(trimmed);
      if (!Number.isFinite(n)) return { ok: false, message: `${field.label} must be a number` };
      return { ok: true, value: n };
    }
    case 'boolean': {
      const v = trimmed.toLowerCase();
      if (['true', '1', 'yes', 'y'].includes(v)) return { ok: true, value: true };
      if (['false', '0', 'no', 'n'].includes(v)) return { ok: true, value: false };
      return { ok: false, message: `${field.label} must be true/false` };
    }
    case 'enum': {
      const lower = trimmed.toLowerCase();
      const match = field.enumValues?.find((v) => v.toLowerCase() === lower);
      if (!match) {
        return {
          ok: false,
          message: `${field.label} must be one of: ${field.enumValues?.join(', ')}`,
        };
      }
      return { ok: true, value: match };
    }
  }
}

interface MappedRow {
  rowNumber: number;
  data: Record<string, unknown>;
  errors: string[];
}

function applyMapping(
  parsed: ParsedCsv,
  mapping: Record<string, string>,
  fields: readonly CsvImportFieldSchema[],
): MappedRow[] {
  return parsed.rows.map((row, idx) => {
    const data: Record<string, unknown> = {};
    const errors: string[] = [];
    for (const field of fields) {
      const sourceCol = mapping[field.key];
      if (!sourceCol || sourceCol === SKIP_VALUE) {
        if (field.required) errors.push(`${field.label} is required (no column mapped)`);
        continue;
      }
      const result = coerceValue(row[sourceCol], field);
      if (!result.ok) {
        errors.push(result.message);
      } else if (result.value !== undefined) {
        data[field.key] = result.value;
      }
    }
    return { rowNumber: idx + 1, data, errors };
  });
}

export function CsvImportWizard({
  open,
  onClose,
  config,
  scope,
  onImported,
}: CsvImportWizardProps) {
  const [step, setStep] = useState<Step>('file');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importResult, setImportResult] = useState<{
    created: number;
    skipped: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);

  const importMutation = useCsvImport();

  const importMutationRef = importMutation.reset;
  // Reset internal state whenever the modal is closed so a re-open starts fresh.
  useEffect(() => {
    if (!open) {
      setStep('file');
      setParsed(null);
      setParseError(null);
      setMapping({});
      setImportResult(null);
      importMutationRef();
    }
  }, [open, importMutationRef]);

  const onFile = useCallback(
    (file: File) => {
      setParseError(null);
      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (results) => {
          if (!results.meta.fields || results.meta.fields.length === 0) {
            setParseError('Could not detect any columns in the CSV.');
            return;
          }
          const headers = results.meta.fields;
          // Normalize all rows to plain Record<string,string> with present headers.
          const rows = (results.data || []).map((row) => {
            const out: Record<string, string> = {};
            for (const h of headers) out[h] = (row[h] ?? '') as string;
            return out;
          });
          if (rows.length === 0) {
            setParseError('CSV contains no data rows.');
            return;
          }
          const next: ParsedCsv = { headers, rows, fileName: file.name };
          setParsed(next);
          setMapping(autoMap(headers, config.fields));
          setStep('mapping');
        },
        error: (err) => setParseError(err.message),
      });
    },
    [config.fields],
  );

  const mappedRows = useMemo(() => {
    if (!parsed) return [];
    return applyMapping(parsed, mapping, config.fields);
  }, [parsed, mapping, config.fields]);

  const validRows = useMemo(() => mappedRows.filter((r) => r.errors.length === 0), [mappedRows]);
  const invalidRows = useMemo(() => mappedRows.filter((r) => r.errors.length > 0), [mappedRows]);

  const requiredMissing = useMemo(() => {
    return config.fields
      .filter((f) => f.required && (!mapping[f.key] || mapping[f.key] === SKIP_VALUE))
      .map((f) => f.label);
  }, [config.fields, mapping]);

  const handleImport = useCallback(async () => {
    if (validRows.length === 0) return;
    try {
      const result = await importMutation.mutateAsync({
        entity: config.entity,
        scope,
        rows: validRows.map((r) => r.data),
      });
      setImportResult(result);
      setStep('result');
      onImported?.(result);
    } catch {
      // The mutation's error state is rendered in the result step.
      setStep('result');
    }
  }, [config.entity, importMutation, onImported, scope, validRows]);

  const stepLabel: Record<Step, string> = {
    file: 'Choose file',
    mapping: 'Map columns',
    preview: 'Preview',
    result: 'Done',
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={config.title}
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between" data-testid="wizard-footer">
          <div className="text-xs text-gray-500">
            Step: {stepLabel[step]} (
            {step === 'file' ? 1 : step === 'mapping' ? 2 : step === 'preview' ? 3 : 4} / 4)
          </div>
          <div className="flex gap-2">
            {step === 'mapping' && (
              <button
                type="button"
                onClick={() => setStep('file')}
                className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              >
                <ChevronLeft size={14} className="inline" /> Back
              </button>
            )}
            {step === 'preview' && (
              <button
                type="button"
                onClick={() => setStep('mapping')}
                className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              >
                <ChevronLeft size={14} className="inline" /> Back
              </button>
            )}
            {step === 'mapping' && (
              <button
                type="button"
                onClick={() => setStep('preview')}
                disabled={requiredMissing.length > 0}
                className="px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
                data-testid="wizard-next-preview"
              >
                Next <ChevronRight size={14} className="inline" />
              </button>
            )}
            {step === 'preview' && (
              <button
                type="button"
                onClick={handleImport}
                disabled={validRows.length === 0 || importMutation.isPending}
                className="px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
                data-testid="wizard-confirm-import"
              >
                {importMutation.isPending ? 'Importing…' : `Import ${validRows.length} row(s)`}
              </button>
            )}
            {step === 'result' && (
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
                data-testid="wizard-close"
              >
                Close
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* ─── Step 1 — Choose file ─────────────────────────── */}
      {step === 'file' && <FileStep onFile={onFile} parseError={parseError} />}

      {/* ─── Step 2 — Map columns ─────────────────────────── */}
      {step === 'mapping' && parsed && (
        <MappingStep
          parsed={parsed}
          fields={config.fields}
          mapping={mapping}
          onChange={setMapping}
          requiredMissing={requiredMissing}
        />
      )}

      {/* ─── Step 3 — Preview ─────────────────────────────── */}
      {step === 'preview' && (
        <PreviewStep
          totalRows={mappedRows.length}
          validCount={validRows.length}
          invalidRows={invalidRows}
        />
      )}

      {/* ─── Step 4 — Result ──────────────────────────────── */}
      {step === 'result' && (
        <ResultStep result={importResult} error={importMutation.error?.message ?? null} />
      )}
    </EntityModal>
  );
}

// ──────────────────────────────────────────────────────────────────
// Sub-components (kept in this file because they are tightly coupled
// to the wizard's local state and not reused elsewhere).
// ──────────────────────────────────────────────────────────────────

function FileStep({
  onFile,
  parseError,
}: {
  onFile: (f: File) => void;
  parseError: string | null;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Pick a CSV file. The first row should contain column headers.
      </p>
      <label
        className="flex flex-col items-center justify-center gap-2 px-6 py-10 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
        data-testid="wizard-file-dropzone"
      >
        <Upload size={28} className="text-gray-400" />
        <span className="text-sm text-gray-600">Click to choose a CSV file</span>
        <input
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          data-testid="wizard-file-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
          }}
        />
      </label>
      {parseError && (
        <div className="text-sm text-red-600" data-testid="wizard-parse-error">
          {parseError}
        </div>
      )}
    </div>
  );
}

function MappingStep({
  parsed,
  fields,
  mapping,
  onChange,
  requiredMissing,
}: {
  parsed: ParsedCsv;
  fields: readonly CsvImportFieldSchema[];
  mapping: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  requiredMissing: string[];
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <FileText size={14} />
        <span>{parsed.fileName}</span>
        <span className="text-gray-400">·</span>
        <span>{parsed.rows.length} rows</span>
      </div>
      <p className="text-sm text-gray-600">
        Match each entity field to a column from your CSV. Unmapped optional fields are skipped.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-auto pr-1">
        {fields.map((field) => (
          <div key={field.key} className="flex flex-col gap-1">
            <label className="text-xs text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            <select
              value={mapping[field.key] ?? SKIP_VALUE}
              onChange={(e) => onChange({ ...mapping, [field.key]: e.target.value })}
              className="px-2 py-1.5 border border-gray-300 rounded text-sm"
              data-testid={`wizard-map-${field.key}`}
            >
              <option value={SKIP_VALUE}>— skip —</option>
              {parsed.headers.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
      {requiredMissing.length > 0 && (
        <div
          className="text-sm text-red-600 flex items-center gap-1"
          data-testid="wizard-required-missing"
        >
          <X size={14} /> Required fields still unmapped: {requiredMissing.join(', ')}
        </div>
      )}
    </div>
  );
}

function PreviewStep({
  totalRows,
  validCount,
  invalidRows,
}: {
  totalRows: number;
  validCount: number;
  invalidRows: MappedRow[];
}) {
  return (
    <div className="space-y-3" data-testid="wizard-preview">
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Total rows" value={totalRows} />
        <SummaryCard label="Will import" value={validCount} color="green" />
        <SummaryCard label="Will skip" value={invalidRows.length} color="red" />
      </div>
      {invalidRows.length > 0 && (
        <div className="border border-red-100 bg-red-50 rounded-md p-3 max-h-64 overflow-auto">
          <p className="text-sm font-medium text-red-700 mb-2">Rows with errors</p>
          <ul className="space-y-1 text-xs text-red-700">
            {invalidRows.slice(0, 50).map((r) => (
              <li key={r.rowNumber}>
                <span className="font-mono">Row {r.rowNumber}:</span> {r.errors.join('; ')}
              </li>
            ))}
            {invalidRows.length > 50 && (
              <li className="text-red-500">…and {invalidRows.length - 50} more</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function ResultStep({
  result,
  error,
}: {
  result: {
    created: number;
    skipped: number;
    errors: Array<{ row: number; message: string }>;
  } | null;
  error: string | null;
}) {
  if (error && !result) {
    return (
      <div className="text-sm text-red-600" data-testid="wizard-result-error">
        Import failed: {error}
      </div>
    );
  }
  if (!result) return null;
  return (
    <div className="space-y-3" data-testid="wizard-result">
      <div className="flex items-center gap-2 text-green-700">
        <Check size={20} />
        <span className="text-sm font-medium">
          Imported {result.created} record{result.created === 1 ? '' : 's'}.
        </span>
      </div>
      {result.skipped > 0 && (
        <div className="text-sm text-amber-700">{result.skipped} row(s) were skipped.</div>
      )}
      {result.errors.length > 0 && (
        <div className="border border-amber-100 bg-amber-50 rounded-md p-3 max-h-64 overflow-auto">
          <ul className="space-y-1 text-xs text-amber-800">
            {result.errors.map((e, i) => (
              <li key={i}>
                <span className="font-mono">Row {e.row}:</span> {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color?: 'green' | 'red';
}) {
  const colorClass =
    color === 'green' ? 'text-green-700' : color === 'red' ? 'text-red-700' : 'text-gray-900';
  return (
    <div className="bg-white border border-gray-200 rounded-md px-4 py-3 text-center">
      <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}
